"use client"

import React, { Suspense, useMemo } from "react"
import Image from "next/image"
import { useUbStorefrontState } from "@/store/ubStorefrontState"
import { storyblokEditable } from "@storyblok/react/rsc"

import { CheckoutContext } from "@/lib/storefront/checkout/checkout-service"
import BasicCard from "@/components/common/BasicCard"
import {
  UbCheckoutReviewPaymentStoryblok,
  UbPaymentMethodContentStoryblok,
} from "@/components/storyblok/component-types"

interface CheckoutReviewPaymentProps {
  blok: UbCheckoutReviewPaymentStoryblok
  checkoutContext: CheckoutContext
}

interface PaymentData {
  displayName?: string
  serviceId?: string
  accountIdentifier?: string
}

export default function CheckoutReviewPaymentClient({ blok, checkoutContext }: CheckoutReviewPaymentProps) {
  const { basketPayments } = useUbStorefrontState()

  const payment: PaymentData = useMemo(() => {
    const payment: PaymentData = {}
    const openTenderPayment = basketPayments?.data?.find((payment) => payment.openTender === true)
    if (openTenderPayment) {
      payment.displayName = basketPayments?.included?.paymentMethod[openTenderPayment.paymentMethod]?.displayName
      payment.serviceId = basketPayments?.included?.paymentMethod[openTenderPayment.paymentMethod]?.serviceID
      payment.accountIdentifier =
        basketPayments?.included?.paymentInstrument[openTenderPayment.paymentInstrument]?.accountIdentifier
    }
    return payment
  }, [basketPayments])

  const paymentContent: UbPaymentMethodContentStoryblok | undefined = useMemo(() => {
    if (typeof blok?.payment_method_content?.[0]?.reference == "string") {
      return undefined
    }
    return blok?.payment_method_content?.[0]?.reference?.content?.body?.find(
      (value: UbPaymentMethodContentStoryblok) => {
        return value.identifier === payment?.serviceId
      }
    )
  }, [blok, payment])

  return (
    <div {...storyblokEditable(blok)} className={"flex w-full flex-col gap-2"}>
      {checkoutContext.userData && (
        <BasicCard className={"top-30 min-h-28 border-none p-0 sm:p-0 lg:top-34"}>
          <div className={"flex w-full flex-row justify-between gap-2"}>
            <Suspense fallback={<div>...</div>}>
              <div className={"flex w-full flex-col gap-2"}>
                <div className={"flex w-full flex-col justify-between gap-2"}>
                  <p className={"text-sm"}>{paymentContent?.title ?? payment.displayName}</p>
                  {paymentContent && (
                    <div className={"relative h-10 w-full md:h-16"}>
                      <Image
                        unoptimized={true}
                        className={"min-h-10 w-auto object-left md:min-h-16"}
                        fill={true}
                        objectFit={"contain"}
                        alt={paymentContent.title ?? ""}
                        src={paymentContent.logos?.[0]?.filename ?? ""}
                      />
                    </div>
                  )}
                </div>
                <p>{payment.accountIdentifier}</p>
              </div>
            </Suspense>
          </div>
        </BasicCard>
      )}
    </div>
  )
}
